/**
 * Every string the Getting Started guide, its dialog, the empty states and the Capture Visit tip
 * show. Tests import from here, so a wording change is one edit.
 *
 * Rules the copy follows (see "Getting Started guide" in `medical-scribe/CLAUDE.md`):
 * - "Ambient Scribe" on first mention, "Scribe" afterwards, "Scribes" only for the page or tab.
 * - Button labels are written exactly as the app shows them.
 * - No unmeasured claims: no percentages, minutes saved or video length.
 * - `**text**` marks a button label to be shown in bold (`GuideText` renders it).
 */
import { AMBIENT_SCRIBE_PLACEHOLDER_BULLETS } from "features/medical-scribe/components/ambient-scribe-purchase-placeholder/ambient-scribe-purchase-placeholder.copy";

/** Reused word for word from the purchase screen, so the two never disagree. */
const HIPAA_LINE =
  AMBIENT_SCRIBE_PLACEHOLDER_BULLETS.find((bullet) =>
    bullet.startsWith("HIPAA")
  ) ?? "HIPAA-compliant and secure";

const MIC_TIP =
  "First time? Your browser will ask to use your microphone. Click **Allow**.";

export const SCRIBE_GUIDE_COPY = {
  // The inline guide is a trial's welcome; its title and lead are the tour's (`introTitle`,
  // `introLead`), so the page and the dialog greet the reader the same way.
  eyebrow: "Free trial",
  trialLine:
    "Use Scribe on a few real visits during your trial and see the notes for yourself.",
  worksInLead: "Works where you already work:",
  worksInChartNotes: "Chart Notes",
  worksInAppointments: "Appointments",
  worksInReports: "Audiology Reports",
  adminLine:
    "Looking for your team's recordings? Pick a name in the Staff filter above.",

  ctaTitle: "Try it at your next visit",
  searchPlaceholder: "Search for a patient",
  ctaHelper:
    "We'll open their Chart Notes. Click **Capture Visit** when the visit starts.",
  checkInTip:
    "Check the patient in first (Arrived), so the note goes into today's visit.",
  reassurances: [
    "Works out of the box. Templates are optional.",
    "You can edit every note.",
    HIPAA_LINE
  ] as readonly string[],
  watchDemo: "Watch the demo",
  hideGuide: "Hide this guide",

  // Step headlines: the tour's, and the page cards' too.
  captureHeadline: "Record the visit in one click",
  micTip: MIC_TIP,
  stopHeadline: "Stop when you're done, pause when you need to",
  reviewHeadline: "Your note, drafted for you",
  afterVisitSummaryHeadline: "A visit summary your patient will understand",
  reportHeadline: "Report summaries, written for you",
  reportButtonWriteSummary: "Use Scribe to Write Summary",
  reportButtonInsertScribe: "Insert Scribe",
  templatesHeadline: "Notes in your format, for every type of visit",
  templatesWizardLine:
    "Pick an appointment type and Suno drafts a template from your clinic's past notes for it.",
  templatesAskAdmin: "Ask your practice admin to set up templates.",

  howScribeWorks: "How Ambient Scribe works",
  tryStepHeadline: "Try it at your next visit",
  tryStepOnPatientPage:
    "Click **Capture Visit** on this page when the visit starts.",
  tryStepFromScribeDetails:
    "Open the patient's **Chart Notes** and click **Capture Visit** when the visit starts.",
  demoFallbackLead: "Video not loading?",
  demoFallbackLink: "Open it in a new tab",
  back: "Back",
  next: "Next",
  done: "Done",
  stepOf: (step: number, total: number) => `Step ${step} of ${total}`,

  hiddenSnackbar:
    "Guide hidden. Open it any time from How Ambient Scribe works.",
  neverRecordedTitle: "No recordings yet.",
  neverRecordedBody:
    "Open a patient's Chart Notes and click **Capture Visit** to record your first visit.",
  filterEmptyTitle: "No recordings match these filters.",
  filterEmptyBodyWithDates:
    "Try a longer date range, another staff member or another clinic.",
  filterEmptyBody: "Try another staff member or clinic.",
  // Under the Scribes page's empty states: an invitation to the tour, pitched at where the reader is.
  inviteNewTitle: "New to Ambient Scribe?",
  inviteNewBody:
    "See how Scribe turns your conversation with a patient into a note that's ready for you to review.",
  // Names only what every practice's tour covers (the basics), never a flag-gated step.
  inviteMoreTitle: "Take the Ambient Scribe tour",
  inviteMoreBody:
    "Walk through each step, from recording a visit to reviewing your note, with tips on pausing, your microphone and the Suno mobile app.",
  patientEmptyTitle: "No recordings for this patient yet.",
  patientEmptyBody:
    "Click Capture Visit when the visit starts. Scribe drafts the note when you click Stop.",

  appointmentCardTitle: "Ambient Scribe",
  appointmentStatusRecorded: "Recorded",
  appointmentStatusRecording: "Recording",
  appointmentStatusNotRecorded: "Not recorded",
  appointmentBodyRecorded:
    "This visit was recorded with Scribe. Click **Open Audio Record Details** to see the latest recording and its note, or expand the card to see them all.",
  openAudioRecordDetails: "Open Audio Record Details",
  appointmentBodyRecording:
    "Scribe is recording this visit. Pause or stop it from the Scribe recording controls.",
  appointmentBodyNotRecorded:
    "Click **Capture Visit** when the visit starts and talk with your patient as usual. Scribe drafts the note when you click Stop.",
  appointmentEmptyTitle: "No recordings for this visit yet.",
  appointmentEmptyBody:
    "Scribe listens while you talk with your patient, then drafts the note when you click Stop. It's a draft, so you can change anything.",
  appointmentShowRecordings: "Show recordings",
  appointmentHideRecordings: "Hide recordings",
  insertScribeSelectPrompt: "Select a recording to see its note here.",
  insertScribeNoRecordings:
    "Once this patient has a recording, you can open its note here.",
  insertScribeNoRecordingsAudiology:
    "Once this patient has a recording, you can generate the report summary from it here.",
  captureVisit: "Capture Visit",
  captureVisitFinishing: "Finishing…",

  coachTitle: "Record this visit",
  coachBody:
    "Click **Capture Visit** and talk with your patient as usual. Click Stop when you're done, and Scribe drafts the note.",
  coachTip: MIC_TIP,
  coachGotIt: "Got it",
  coachCloseLabel: "Close tip"
} as const;

/**
 * The tour: the "How Ambient Scribe works" dialog, and the Scribes page's inline guide, which shows
 * the same steps in the same words (minus "Good to know"). Same rules as above, and every claim is
 * something the app does today (each line names the control the reader will see).
 */
export const SCRIBE_GUIDE_TOUR_COPY = {
  demoTitle: "Watch the demo",
  demoSubtitle: "See Scribe in a real visit",
  groupBasics: "The basics",
  groupMore: "Do more with Scribe",
  howToTitle: "How to do it",
  goodToKnowTitle: "Good to know",
  linksTitle: "Open in a new tab",
  opensInNewTab: "(opens in a new tab)",

  introTitle: "Welcome to Ambient Scribe",
  // The inline guide's lead too.
  introLead:
    "Scribe listens while you talk with your patient and drafts the note for you, so you can focus on the person in front of you.",
  // The dialog's only: it names the dialog's own buttons.
  introTourHint:
    "This short tour covers the basics first, then the extras. Click **Next** to follow along, or pick any topic on the left.",

  captureNav: "Record the visit",
  captureBenefit:
    "Keep your eyes on your patient, not the keyboard. Scribe listens in the background while you have a normal conversation.",
  captureOpenWithAppointments:
    "Open the patient's **Chart Notes**, today's appointment or their **Scribes** tab.",
  captureOpen: "Open the patient's **Chart Notes** or their **Scribes** tab.",
  captureCheckIn:
    "Check the patient in first (**Arrived**), so the note goes into today's visit.",
  captureClick: "When the visit starts, click **Capture Visit**.",
  captureTalk:
    "Talk with your patient as usual. No dictating and no special phrases.",
  captureRecordingBar:
    "A recording bar shows while Scribe is listening, so you always know it's on.",

  stopNav: "Stop, pause, resume",
  stopBenefit:
    "Visits don't always go in a straight line. Step out, come back and carry on, all in one recording.",
  stopDone: "Done? Click the Stop button (the square), then **Yes**.",
  stopPause:
    "Need a moment? Click the Pause button (two bars). Click the microphone button to resume, and Scribe carries on in the same recording.",
  stopPostpone:
    "Can't finish now? While paused, click **Postpone**. Later, open the recording from the patient's **Scribes** tab and click **Resume**, or click **Complete Scribe** to get the note.",
  stopNoteReady:
    "A few moments after you stop, Scribe writes the note from the conversation.",
  // Captions on the tour's picture of the recording bar, one per state it shows.
  stopPictureRecording: "Recording",
  stopPicturePaused: "Paused",
  stopPictureStopping: "After Stop",

  reviewNav: "Review your note",
  reviewBenefit:
    "Scribe turns the conversation into a structured SOAP note. You review and adjust instead of writing from scratch.",
  reviewOpen:
    "Open the visit's chart note. Scribe puts the note there for you.",
  reviewInsert:
    "No chart note for this visit? Open one and click **Insert Scribe** to add the note.",
  reviewEdit:
    "Read it through and change anything you like. It's a draft, and you have the final say.",
  reviewContext: "Anything you type in the chart note is taken into account.",
  reviewApply:
    "Want a different format? Pick another template on the note and click **Apply** to rewrite it.",
  reviewTranscript:
    "Open any recording from the patient's **Scribes** tab to read its transcript.",

  setupNav: "Microphone & mobile app",
  setupHeadline: "Clear audio, at your desk or on your phone",
  setupBenefit:
    "The clearer Scribe hears the visit, the better the note. Check your microphone once and you're set.",
  setupMicTest:
    "Before your first visit, click **Mic Test** on the Scribe recording bar to check your microphone.",
  setupChooseMic:
    "Click the gear on the same bar to choose which microphone Scribe uses.",
  setupPlaceMic:
    "Put the microphone where it can hear you both, like on the desk between you and your patient.",
  setupSilence: "If Scribe can't hear anything, it lets you know.",
  mobileTitle: "Record on your phone with the Suno app",
  mobileBody:
    "In the booth, the fitting room or on a home visit, your phone is the microphone. The note lands in the same patient chart.",
  mobileAppStore: "App Store",
  // The login page's own label: the link is Suno's Android download, not a Google Play listing.
  mobileAndroid: "Android",

  afterVisitSummaryNav: "After Visit Summary",
  afterVisitSummaryBenefit:
    "Send every patient home with a clear, plain-language recap of their visit, with no extra writing for you.",
  afterVisitSummaryDrafted:
    "When the recording is done, Scribe drafts an After Visit Summary in plain language.",
  afterVisitSummaryOpen:
    "On the chart note, click **After Visit Summary** to read it and make any changes.",
  afterVisitSummarySend:
    "Email it to your patient, or print it with your practice logo.",
  afterVisitSummaryAppointment: "You'll also find it on the appointment page.",
  afterVisitSummaryTemplates:
    "Each appointment type can have its own summary template, so a fitting summary reads differently from a hearing test summary.",

  reportNav: "Audiology reports",
  reportBenefit:
    "Scribe combines what was said in the visit with the test results, so the report summary is ready for you to review.",
  reportOpen: "Open the patient's audiology report.",
  reportWrite: "In the summary section, click **Use Scribe to Write Summary**.",
  reportGenerate:
    "Pick the visit's recording, click **Generate Summary**, then insert it into the report.",
  reportTemplate:
    "Pick an Audiology Report template to shape the summary your way, or keep the built-in format: History, Results and Recommendations.",
  reportBuiltIn: "Scribe writes it as History, Results and Recommendations.",
  reportBenefitInsert:
    "Bring the visit's note into the audiology report without retyping it.",
  reportInsertScribe:
    "Click **Insert Scribe** to add the visit's Scribe note to the report.",

  templatesNav: "Templates",
  templatesBenefit:
    "Make Scribe write the way your clinic already writes, with the right format for each type of visit.",
  templatesNew:
    "Open **Manage Scribe Templates** and click **New Scribe Template**.",
  templatesLinkTypes:
    "Link it to appointment types, like Hearing Test or Hearing Aid Fitting. Scribe uses it for those visits on its own.",
  templatesWriteYourWay:
    "Lay it out the way your clinic writes notes: the sections, their order and the detail you expect.",
  templatesDefault: "Make one template your default for every other visit.",
  templatesOptional:
    "Templates are optional. Out of the box, Scribe uses the Suno Default SOAP Template.",
  templatesKindsLead: "Make templates for",
  templatesKindChartNotes: "chart notes",
  templatesKindReports: "audiology report summaries",
  templatesKindAfterVisitSummaries: "After Visit Summaries",

  tryNav: "Try it at your next visit",

  linkChartNotes: "This patient's Chart Notes",
  linkAppointments: "This patient's appointments",
  linkPatientScribes: "This patient's Scribes",
  linkAudiologyReports: "This patient's audiology reports",
  linkScribeTemplates: "Manage Scribe Templates",
  linkAppointmentTypes: "Appointment Types"
} as const;
